import React, { useState, useEffect, useRef, useMemo} from 'react';
import { walletAmount, WalletValue, useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpFromLine, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useChatLayout } from '../context/ChatLayoutContext';

// Backend URL Settings
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');

// Difficulty Settings
const DIFFICULTIES = {
  easy: { name: 'Easy', cols: 4, safe: 3, multiplier: 1.30 },
  normal: { name: 'Normal', cols: 3, safe: 2, multiplier: 1.45 },
  hard: { name: 'Hard', cols: 2, safe: 1, multiplier: 1.90 }
};

const ROWS = 8;

// ==========================================
// YENİ: K, M, B FORMATLAYICILARI (ÇİFT YÖNLÜ)
// ==========================================
const parseAmount = (valStr) => {
  if (!valStr) return 0;
  // ⚠️ BİNLİK AYIRICI TEMİZLENMELİ. formatAmount() büyük sayıları
  // toLocaleString ile üretiyor ("1,457,545"); parseFloat virgülde durup
  // 1 döndürüyordu. MAX butonuna basan kullanıcı 1 birim bahis oynuyordu.
  let str = valStr.toString().trim().toLowerCase().replace(/[,\s]/g, '');
  let multiplier = 1;
  if (str.endsWith('k')) { multiplier = 1000; str = str.slice(0, -1); }
  else if (str.endsWith('m')) { multiplier = 1000000; str = str.slice(0, -1); }
  else if (str.endsWith('b')) { multiplier = 1000000000; str = str.slice(0, -1); }
  
  const parsedValue = parseFloat(str);
  return isNaN(parsedValue) ? 0 : Math.floor(parsedValue * multiplier);
};

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === 0) return '';
  // Küsüratlı durumlarda gereksiz sıfırları temizlemek için parseFloat kullanıyoruz (Örn: 1.50K -> 1.5K)
  if (amount >= 1000000) return amount.toLocaleString();
  if (amount >= 1000) return parseFloat((amount / 1000).toFixed(2)) + 'K';
  return amount.toString();
};

export const Tower = () => {
  const { user, token, balance, fmtMoney, wallet, betToRaw, rawToBet, betLimits } = useAuth();
  const wsRef = useRef(null);
  
  // ==========================================
  // AUDIO EFFECTS (Audio Refs)
  // ==========================================
  const explosionSound = useRef(typeof Audio !== "undefined" ? new Audio('/explosion.mp3') : null);
  const successSound = useRef(typeof Audio !== "undefined" ? new Audio('/towerWin.wav') : null);

  // Set audio volumes
  useEffect(() => {
    if (explosionSound.current) explosionSound.current.volume = 0.8;
    if (successSound.current) successSound.current.volume = 0.6;
  }, []);

  // Safe play sound function
  const playSound = (audioRef) => {
    if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log("Audio blocked:", e));
    }
  };

  const [betAmount, setBetAmount] = useState('');
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const [difficulty, setDifficulty] = useState('normal');
  
  const [phase, setPhase] = useState('idle'); 
  const [currentRow, setCurrentRow] = useState(0);
  const [currentMultiplier, setCurrentMultiplier] = useState(1.00);
  
  const [towerData, setTowerData] = useState([]);
  const [serverHash, setServerHash] = useState('');
  const [serverSeed, setServerSeed] = useState('');

  const initializeTower = () => {
    const newTower = Array.from({ length: ROWS }).map(() => ({
      revealed: false, safeIdx: [], bombIdx: [], playerChoice: null
    }));
    setTowerData(newTower);
    setCurrentRow(0);
    setCurrentMultiplier(1.00);
    setServerHash('');
    setServerSeed('');
  };

  useEffect(() => {
    if (phase === 'idle') initializeTower();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficulty, phase]);

  useEffect(() => {
    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/ws/tower/${token}`);
    wsRef.current = ws;

    ws.onopen = () => console.log('🗼 Connected to Tower Server!');

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'TOWER_START') {
          setPhase('playing');
          setCurrentRow(0);
          setCurrentMultiplier(1.00);
          setServerHash(data.server_seed_hash);
          setServerSeed('');
          
          setTowerData(Array.from({ length: ROWS }).map(() => ({
            revealed: false, safeIdx: [], bombIdx: [], playerChoice: null
          })));
        }
        else if (data.type === 'TOWER_NEXT_ROW') {
          playSound(successSound); 
          // ⚠️ Yerinde mutasyon YOK: `[...prev]` satır nesnelerini
          // paylaştığı için önceki state de değişiyordu.
          setTowerData(prev => {
            const prevRow = data.current_row - 1;
            return prev.map((row, i) => i === prevRow
              ? { ...row, revealed: true, safeIdx: [row.playerChoice] }
              : row);
          });
          setCurrentRow(data.current_row);
          setCurrentMultiplier(data.current_multiplier);
        }
        else if (data.type === 'TOWER_CRASH') {
          setPhase('crashed');
          setServerSeed(data.server_seed);
          playSound(explosionSound); 
          
          // ⚠️ Yerinde mutasyon YOK (bkz. yukarıdaki not).
          setTowerData(prev => prev.map((row, idx) => {
            const mapRow = data.tower_map[idx];
            return mapRow
              ? { ...row, revealed: true, safeIdx: mapRow.safes, bombIdx: mapRow.bombs }
              : row;
          }));
        }
        else if (data.type === 'TOWER_WIN') {
          setPhase('cashed_out');
          setServerSeed(data.server_seed);
          setCurrentMultiplier(data.multiplier);
          playSound(successSound); 
          
          // ⚠️ Yerinde mutasyon YOK (bkz. yukarıdaki not).
          setTowerData(prev => prev.map((row, idx) => {
            const mapRow = data.tower_map[idx];
            return mapRow
              ? { ...row, revealed: true, safeIdx: mapRow.safes, bombIdx: mapRow.bombs }
              : row;
          }));
          
          // YENİ: Kazanç mesajını formatlayarak gösterir
        }
        else if (data.type === 'ERROR') {
          toast.error(data.message);
        }
      } catch (error) { console.error("WS Parse Error:", error); }
    };

    return () => ws.close();
  }, [token]);

  // YENİ: K, M, B Destekli Hızlı Buton Fonksiyonları
  const addBetAmount = (val) => {
    const current = parseAmount(betAmount);
    setBetAmount(String(current + val));
  };
  
  const multiplyBet = (mult) => {
    const current = parseAmount(betAmount);
    setBetAmount(String(Math.floor(current * mult)));
  };

  const placeBet = () => {
    // 🎯 Kullanıcı AKTİF CÜZDANIN biriminde yazar (Robux → site birimi,
    // BB Coin → BB). betToRaw saklama birimine çevirir: BB'de ×1000.
    // Bu satır olmadan "10" yazan oyuncu 10 mili-BB (0,01 BB) oynardı.
    const amount = betToRaw(parseAmount(betAmount)); // YENİ: String'i saf Integer'a çevirir
    // ⚠️ Limitler cüzdana göre; "1 – 100.000" SİTE birimidir ve
    // BB Coin bahislerini haksız yere reddederdi.
    if (!amount || amount < betLimits.min_bet || amount > betLimits.max_bet) {
      return toast.error(`Bet must be between ${walletAmount(betLimits.min_bet, wallet)} and ${walletAmount(betLimits.max_bet, wallet)}.`);
    }
    // 🍀 AKTİF CÜZDANIN bakiyesiyle karşılaştır (LC'de mili-LC).
    if (amount > balance) return toast.error("Insufficient balance!");
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'PLACE_BET',
        amount: amount,
        difficulty: difficulty
      }));
    } else {
      toast.error("Server connection lost!");
    }
  };

  const handleTileClick = (rowIndex, colIndex) => {
    if (phase !== 'playing' || rowIndex !== currentRow) return;

    setTowerData(prev => {
      // ⚠️ Yerinde mutasyon YOK: değişen satır yeniden oluşturulur.
      return prev.map((row, i) => i === rowIndex
        ? { ...row, playerChoice: colIndex } : row);
    });

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'CLICK_TILE', row: rowIndex, col: colIndex }));
    }
  };

  const handleCashout = () => {
    if (phase !== 'playing' || currentRow === 0) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'CASHOUT' }));
    }
  };

  // ⚡ towerData değişmedikçe yeniden hesaplanmaz. Eskiden her render'da
  // (her WS mesajında) tüm dizi kopyalanıp ters çevriliyordu ve 8+ satır ×
  // 2-4 framer-motion kutusu yeniden çiziliyordu — oyun sırasında takılma.
  const reversedTower = useMemo(() => [...towerData].reverse(), [towerData]);

  // ⚡ Saf hesap; difficulty değişmedikçe sabit.
  const boxWidthCls = useMemo(() => {
    if (difficulty === 'hard') return 'max-w-[320px] md:max-w-sm';
    if (difficulty === 'normal') return 'max-w-[380px] md:max-w-md';
    return 'max-w-[440px] md:max-w-lg';
  }, [difficulty]);
  const getBoxWidth = () => boxWidthCls;

  return (
    <div className="min-h-screen bg-[#0E0F11]" data-testid="tower-page">
      <div className="max-w-[1800px] mx-auto px-4 pt-24 pb-8">
        <div className={`flex flex-col lg:flex-row gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
          <main className="flex-1 flex flex-col lg:flex-row gap-6 min-w-0 lg:h-[calc(100vh-120px)]">
            <div className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-4 h-full">
              <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-4 shadow-lg h-full flex flex-col">
                <div className="space-y-6 flex-1">
                  <div>
                    <label className="text-xs text-zinc-400 font-bold mb-2 block">Difficulty Level</label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.keys(DIFFICULTIES).map((diff) => (
                        <button
                          key={diff}
                          onClick={() => setDifficulty(diff)}
                          disabled={phase === 'playing'}
                          className={`py-2 rounded-lg text-[10px] md:text-xs font-bold uppercase transition-all ${
                            difficulty === diff 
                            ? 'bg-[#B059E8] text-white shadow-[0_0_15px_rgba(176,89,232,0.4)]' 
                            : 'bg-[#2A2D31]/50 text-zinc-400 hover:bg-[#33363b] hover:text-white disabled:opacity-50'
                          }`}
                        >
                          {DIFFICULTIES[diff].name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-zinc-400 font-bold mb-1.5 block">Bet Amount</label>
                    <div className="flex bg-[#15171a] border border-[#2A2D31] rounded-xl overflow-hidden focus-within:border-[#B059E8] transition-colors">
                      <div className="pl-3 flex items-center justify-center">
                        <span className="text-zinc-500 font-bold">$</span>
                      </div>
                      <input
                        type="text" // YENİ: type="number" kaldırıldı ki harf yazılabilsin
                        value={betAmount}
                        onChange={(e) => setBetAmount(e.target.value)}
                        disabled={phase === 'playing'}
                        className="w-full bg-transparent py-3 px-2 text-white font-bold outline-none disabled:opacity-50 uppercase"
                        placeholder="1"
                      />
                      <div className="flex border-l border-[#2A2D31]">
                        {/* YENİ: Butonlar artık harfli formatlama yapar */}
                        <button onClick={() => addBetAmount(10)} disabled={phase === 'playing'} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors border-r border-[#2A2D31]">+10</button>
                        <button onClick={() => multiplyBet(0.5)} disabled={phase === 'playing'} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors border-r border-[#2A2D31]">1/2</button>
                        <button onClick={() => multiplyBet(2)} disabled={phase === 'playing'} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors">2x</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-auto pt-6">
                  {phase === 'idle' || phase === 'crashed' || phase === 'cashed_out' ? (
                    <button 
                      onClick={placeBet}
                      className="w-full py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-all shadow-lg bg-gradient-to-r from-[#7b2fc4] to-[#B059E8] hover:from-[#B059E8] hover:to-[#c98bf0] text-white"
                    >
                      {phase === 'crashed' || phase === 'cashed_out' ? 'Play Again' : 'Start Game'}
                    </button>
                  ) : (
                    <button 
                      onClick={handleCashout}
                      disabled={currentRow === 0} 
                      className="w-full py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-all shadow-lg bg-gradient-to-r from-[#16A34A] to-[#2ecf6e] hover:from-[#2ecf6e] hover:to-[#5ee08f] text-white disabled:opacity-50 disabled:grayscale flex flex-col items-center justify-center leading-none"
                    >
                      <span className="mb-1">CASHOUT</span>
                      {currentRow > 0 && (
                        <span className="text-sm opacity-90 mm-num">
                          {/* YENİ: Anlık cashout miktarını formatlı gösterir */}
                          <WalletValue amount={Math.floor(betToRaw(parseAmount(betAmount)) * currentMultiplier)} wallet={wallet} />
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* HARİTA KISMI */}
            <div className="flex-1 flex flex-col items-center justify-center bg-[#1A1D20] rounded-2xl border border-[#2A2D31] shadow-2xl relative overflow-hidden h-full p-4 lg:p-6">
              <div className="absolute top-4 left-4 md:top-6 md:left-6 flex flex-col z-10">
                <span className="text-zinc-500 text-xs md:text-sm font-bold uppercase tracking-widest mb-0.5">Current Multiplier</span>
                <span className={`text-3xl md:text-4xl font-heading font-bold ${phase === 'crashed' ? 'text-rose-500' : 'text-[#2ecf6e]'} drop-shadow-lg`}>
                  {currentMultiplier.toFixed(2)}x
                </span>
              </div>

              <div className={`flex flex-col gap-1.5 md:gap-2 w-full ${getBoxWidth()} mt-12 md:mt-8 mb-12 md:mb-8`}>
                {reversedTower.map((row, reverseIdx) => {
                  const actualRowIndex = ROWS - 1 - reverseIdx;
                  const isCurrentRow = phase === 'playing' && actualRowIndex === currentRow;
                  const isPassedRow = actualRowIndex < currentRow;
                  const rowCols = DIFFICULTIES[difficulty].cols;
                  const rowMultiplier = Math.pow(DIFFICULTIES[difficulty].multiplier, actualRowIndex + 1).toFixed(2);

                  return (
                    <div key={actualRowIndex} className="flex items-stretch w-full">
                      <div className={`flex items-center justify-end flex-shrink-0 w-10 md:w-14 pr-2 md:pr-3 mm-num text-[10px] md:text-xs font-bold ${isCurrentRow ? 'text-white' : isPassedRow ? 'text-[#16A34A]' : 'text-zinc-600'}`}>
                        {rowMultiplier}x
                      </div>

                      <div className="flex flex-1 gap-1.5 md:gap-2">
                        {Array.from({ length: rowCols }).map((_, colIndex) => {
                          const isRevealed = row.revealed || phase === 'crashed' || phase === 'cashed_out';
                          const isBomb = row.bombIdx.includes(colIndex);
                          const isSafe = row.safeIdx.includes(colIndex);
                          const isPlayerChoice = row.playerChoice === colIndex;

                          let boxStyle = "bg-[#2A2D31]/40 border-[#2A2D31]"; 
                          
                          if (isCurrentRow) {
                            boxStyle = "bg-[#2A2D31]/80 border-[#B059E8]/50 cursor-pointer hover:bg-[#B059E8]/20 hover:border-[#B059E8] shadow-[0_0_15px_rgba(176,89,232,0.1)]";
                          } else if (!isRevealed && actualRowIndex > currentRow) {
                            boxStyle = "bg-[#15171a] border-[#2A2D31]/30 opacity-50"; 
                          }

                          if (isRevealed) {
                            if (isSafe) {
                              boxStyle = isPlayerChoice 
                                ? "bg-[#16A34A]/20 border-[#16A34A] shadow-[0_0_20px_rgba(22,163,74,0.3)]" 
                                : "bg-[#16A34A]/10 border-[#16A34A]/30 opacity-60"; 
                            } else if (isBomb) {
                              boxStyle = isPlayerChoice 
                                ? "bg-rose-500/20 border-rose-500 shadow-[0_0_20px_rgba(244,63,94,0.3)]" 
                                : "bg-rose-500/10 border-rose-500/30 opacity-60"; 
                            }
                          }

                          return (
                            <div
                              key={colIndex}
                              onClick={() => handleTileClick(actualRowIndex, colIndex)}
                              /* ⚡ motion.div DEĞİL: hiçbir framer prop'u yoktu,
                                 sarmalama maliyeti boşunaydı (27 eleman).
                                 transition-all → transition-colors: kutuda
                                 değişen tek şey renk/kenarlık. */
                              className={`flex-1 h-10 md:h-12 lg:h-14 rounded-xl border-2 flex items-center justify-center transition-colors duration-300 relative overflow-hidden ${boxStyle}`}
                            >
                              <AnimatePresence>
                                {isRevealed && (
                                  <motion.div
                                    /* ⚡ rotate KALDIRILDI: patlamada ~27 kutu
                                       aynı anda dönüyordu ve her kare layout+
                                       paint zorluyordu. Ölçek tek başına aynı
                                       "ortaya çıkma" hissini veriyor. */
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ duration: 0.18, ease: 'easeOut' }}
                                    className="z-10 flex items-center justify-center"
                                  >
                                    {isBomb ? (
                                      <img src="/coin.png" alt="Coin" className={`w-6 h-6 md:w-8 md:h-8 object-contain ${!isPlayerChoice && 'opacity-50 grayscale'}`} />
                                    ) : (
                                      <img src="/logo.png" alt="Emerald" className={`w-6 h-6 md:w-8 md:h-8 object-contain drop-shadow-[0_0_10px_rgba(22,163,74,0.5)] ${!isPlayerChoice && 'opacity-50 grayscale'}`} />
                                    )}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                              {/* ⚡ Sonsuz yanıp sönme YALNIZCA aktif katta ve
                                  yalnız açılmamışken; patlama anında tüm
                                  kutular açıldığı için hepsi otomatik durur. */}
                              {isCurrentRow && !isRevealed && (
                                <motion.div animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} className="absolute inset-0 bg-[#B059E8]/10" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {phase === 'crashed' && (
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="absolute bottom-12 md:bottom-16 bg-rose-500 text-white px-6 md:px-8 py-2 md:py-3 rounded-full font-bold text-base md:text-lg shadow-[0_0_30px_rgba(244,63,94,0.5)] z-20">
                  Crashed!
                </motion.div>
              )}
              {phase === 'cashed_out' && (
                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="absolute bottom-12 md:bottom-16 bg-[#16A34A] text-white px-6 md:px-8 py-2 md:py-3 rounded-full font-bold text-base md:text-lg shadow-[0_0_30px_rgba(22,163,74,0.5)] flex items-center gap-2 z-20">
                  <ArrowUpFromLine className="w-4 h-4 md:w-5 md:h-5" /> 
                  {/* YENİ: Ortadaki kazanç yazısı formatlandı */}
                  Won <WalletValue amount={Math.floor(betToRaw(parseAmount(betAmount)) * currentMultiplier)} wallet={wallet} />!
                </motion.div>
              )}

              <div className="absolute bottom-4 left-4 right-4 flex flex-col md:flex-row items-center justify-between gap-2 text-[10px] md:text-xs text-zinc-500 mm-num pointer-events-none px-2">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
                  <span>Provably Fair</span>
                </div>
                {serverHash && (
                  <div className="flex gap-4 w-full md:w-auto overflow-hidden opacity-70">
                    <span className="truncate" title={serverHash}>
                      <span className="text-zinc-600">Hash:</span> {serverHash.substring(0, 16)}...
                    </span>
                    {serverSeed && (
                      <span className="truncate text-[#16A34A]" title={serverSeed}>
                        <span className="text-[#15913f]">Seed:</span> {serverSeed.substring(0, 16)}...
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};